// ===========================================
// Email Settings Page
// Dedicated page for email branding and custom domain configuration
// ===========================================

import { useState } from 'react';
import { Alert, Box, Tabs, Tab, Paper } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { Palette as PaletteIcon, Dns as DnsIcon } from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import EmailBrandingTab from '../../components/admin/settings/EmailBrandingTab';
import EmailDomainTab from '../../components/admin/settings/EmailDomainTab';

// ===========================================
// TYPES
// ===========================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ===========================================
// TAB PANEL COMPONENT
// ===========================================

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-settings-tabpanel-${index}`}
      aria-labelledby={`email-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `email-settings-tab-${index}`,
    'aria-controls': `email-settings-tabpanel-${index}`,
  };
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function EmailSettingsPage() {
  const { can } = usePermissions();
  const canWrite = can('communications_settings', 'write');
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <PageHeader
        title="Email Settings"
        subtitle="Configure email branding, templates, and customize how your school's emails appear to recipients"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Communications' },
          { label: 'Email Settings' },
        ]}
      />

      {!canWrite && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view these settings. Changing them needs the Communications Settings permission.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<PaletteIcon />}
            iconPosition="start"
            label="Branding"
            {...a11yProps(0)}
          />
          <Tab
            icon={<DnsIcon />}
            iconPosition="start"
            label="Custom Domain"
            {...a11yProps(1)}
          />
        </Tabs>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <EmailBrandingTab readOnly={!canWrite} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <EmailDomainTab />
      </TabPanel>
    </Box>
  );
}
